import type { ReactNode } from "react";
import { MapPin, Tags } from "lucide-react";
import type { MockProduct } from "../utils/landingFeatures";
import { getProductImageUrl } from "../utils/imageUtils";

export const LANDING_DISPLAY =
  "font-[Manrope,'SF_Pro_Display',system-ui,sans-serif] tracking-[-0.02em]";

export function HeroDealCard({ product }: { product: MockProduct }) {
  const discount =
    product.msrp > 0 && product.price > 0
      ? Math.round(((product.msrp - product.price) / product.msrp) * 100)
      : 0;

  return (
    <div className="flex gap-3 rounded-xl bg-white/[0.04] border border-white/[0.08] p-3 hover:bg-white/[0.07] hover:border-[#5907c2]/30 transition-colors">
      <div className="relative w-[72px] h-[72px] rounded-lg bg-white/[0.06] border border-white/10 flex-shrink-0 overflow-hidden">
        <img src={getProductImageUrl(product.image, "400")} alt="" className="w-full h-full object-contain p-1.5" />
        {product.badge && (
          <span className="absolute bottom-0 inset-x-0 text-center text-[8px] font-bold text-white bg-gradient-to-r from-[#5907c2] to-[#9d24eb] py-0.5">
            {product.badge}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <p className="text-[13px] font-semibold text-white leading-tight line-clamp-2">
          {product.name}
        </p>
        <p className="text-[11px] text-white/45 mt-0.5 truncate">{product.storeLabel}</p>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="text-[17px] font-extrabold text-white leading-none">
            ${product.price.toFixed(2)}
          </span>
          {product.msrp > product.price && (
            <span className="text-[11px] text-white/35 line-through">
              ${product.msrp.toFixed(2)}
            </span>
          )}
          {discount > 0 && (
            <span className="text-[10px] font-bold text-[#d131f3] bg-[#5907c2]/20 border border-[#5907c2]/30 px-1.5 py-0.5 rounded-full">
              {discount}% off
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-white/40">
          {product.aisle && <span>Aisle {product.aisle}</span>}
          {product.aisle && <span>·</span>}
          <span className="flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-emerald-400" />
            {product.stock} in stock
          </span>
        </div>
      </div>
    </div>
  );
}

export function HeroAppPreview({ className = "" }: { className?: string }) {
  return (
    <div className={`relative rounded-[2.5rem] border border-white/10 bg-gradient-to-b from-white/[0.07] to-white/[0.02] p-3 shadow-[0_0_60px_-15px_rgba(89,7,194,0.35)] backdrop-blur-sm ${className}`}>
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="/hero-app-demo-poster.jpg"
        className="w-full h-auto rounded-[2rem] border border-white/10 shadow-inner"
        aria-label="DealHawk app demo"
      >
        <source src="/hero-app-demo.webm" type="video/webm" />
        <source src="/hero-app-demo.mp4" type="video/mp4" />
      </video>
    </div>
  );
}

export function HeroDealPreview({
  label,
  products,
  children,
}: {
  label: string;
  products?: MockProduct[];
  children?: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.07] to-white/[0.02] p-4 shadow-[0_0_60px_-15px_rgba(89,7,194,0.35)] backdrop-blur-sm">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-[#d131f3] animate-pulse" />
        <span className="text-[11px] font-semibold text-white/70 uppercase tracking-wider">
          {label}
        </span>
      </div>
      <div className="space-y-2">
        {children ??
          products?.map((product) => <HeroDealCard key={product.upc} product={product} />)}
      </div>
    </div>
  );
}

export function HighlightsStrip() {
  const items = [
    { icon: Tags, label: "7+ retailers" },
    { icon: MapPin, label: "Aisle-level locations" },
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-4 mb-10">
      {items.map(({ icon: Icon, label }) => (
        <div
          key={label}
          className="flex items-center gap-3 rounded-xl bg-white border border-slate-200 px-5 py-4 shadow-sm"
        >
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-violet-100 text-[#5907c2] flex-shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <span className="text-sm font-semibold text-slate-800">{label}</span>
        </div>
      ))}
    </div>
  );
}
